import {
  DeleteOutlined,
  DragOutlined,
  ExportOutlined,
  FileTextOutlined,
  MessageOutlined,
  PictureOutlined,
  PlusOutlined,
  ReloadOutlined,
  RobotOutlined,
  TeamOutlined,
  TrophyOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  IJourneyStep,
  IJourneyStepConfigs,
  IJourneyStepMetadata,
  JOURNEY_STEP_TYPE_OPTIONS,
  JourneyStepType,
} from "@model";
import {
  Button,
  Checkbox,
  Form,
  FormListFieldData,
  Input,
  InputNumber,
  Select,
  Tabs,
  Tooltip,
  message,
} from "antd";
import type { FormInstance } from "antd/es/form";
import {
  CSSProperties,
  DragEvent,
  ReactNode,
  useEffect,
  useState,
} from "react";
import { JOURNEY_LANGUAGES, getStepDisplayTitle } from "./journeyLanguages";
import { voiceCodeForLanguageKey } from "../ChatLesson/chatLessonLanguages";
import MediaTalkStepFields from "./MediaTalkStepFields";

const METADATA_SECTION_STYLE: CSSProperties = {
  background: "#f5f5f5",
  border: "1px solid #ebebeb",
  borderRadius: 6,
  padding: "8px 10px 2px",
  marginBottom: 8,
};

const METADATA_SECTION_LABEL_STYLE: CSSProperties = {
  marginBottom: 8,
  fontSize: 11,
  fontWeight: 600,
  color: "rgba(0, 0, 0, 0.55)",
};

function StepConfigsField({
  value: _value,
  onChange: _onChange,
}: {
  value?: IJourneyStepConfigs;
  onChange?: (value?: IJourneyStepConfigs) => void;
}) {
  return null;
}

type MetadataTranslation = Record<
  string,
  { title?: string; description?: string; starterText?: string }
>;

function mergeMetadataTranslations<
  T extends {
    title?: string;
    description?: string;
    starterText?: string;
  },
>(current: Record<string, T> | undefined, translations: MetadataTranslation) {
  const next = { ...(current ?? {}) };
  for (const [langKey, fields] of Object.entries(translations)) {
    if (langKey === "en" || !fields) continue;
    next[langKey] = {
      ...next[langKey],
      ...(fields.title?.trim() ? { title: fields.title.trim() } : {}),
      ...(fields.description?.trim()
        ? { description: fields.description.trim() }
        : {}),
      ...(fields.starterText?.trim()
        ? { starterText: fields.starterText.trim() }
        : {}),
    };
  }
  return next;
}

function renderMetadataTranslateButton(options: {
  loading: boolean;
  onClick: () => void;
  tooltip: string;
}) {
  return (
    <Tooltip title={options.tooltip}>
      <Button
        type="text"
        size="small"
        aria-label={options.tooltip}
        icon={<RobotOutlined />}
        loading={options.loading}
        onClick={options.onClick}
      />
    </Tooltip>
  );
}

const stepTypeMeta: Record<
  JourneyStepType,
  { color: string; icon: ReactNode; short: string }
> = {
  [JourneyStepType.ChatLesson]: {
    color: "#1890ff",
    icon: <MessageOutlined />,
    short: "Lesson",
  },
  [JourneyStepType.WordLearning]: {
    color: "#52c41a",
    icon: <FileTextOutlined />,
    short: "Words",
  },
  [JourneyStepType.CharacterTalk]: {
    color: "#722ed1",
    icon: <UserOutlined />,
    short: "Character",
  },
  [JourneyStepType.MediaTalk]: {
    color: "#eb2f96",
    icon: <PictureOutlined />,
    short: "Media talk",
  },
  [JourneyStepType.LevelTest]: {
    color: "#fa8c16",
    icon: <TrophyOutlined />,
    short: "Level test",
  },
  [JourneyStepType.PartnerTalk]: {
    color: "#13c2c2",
    icon: <TeamOutlined />,
    short: "Partner",
  },
};

interface SelectOption {
  value: string;
  label: string;
}

type StepListFieldProps = {
  name: number;
  restField: Omit<FormListFieldData, "name" | "key">;
};

export const newStepId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `step-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export const defaultJourneyStep = (order: number): IJourneyStep => ({
  stepId: newStepId(),
  type: JourneyStepType.ChatLesson,
  isOptional: false,
  order,
  xpReward: 0,
});

function adjustIndexAfterMove(
  activeIndex: number,
  from: number,
  to: number,
): number {
  if (activeIndex === from) return to;
  if (from < activeIndex && to >= activeIndex) return activeIndex - 1;
  if (from > activeIndex && to <= activeIndex) return activeIndex + 1;
  return activeIndex;
}

const ChatLessonStepField = ({
  name,
  restField,
  loadingStepOptions,
  lessonOptions,
  onRefreshLessons,
}: StepListFieldProps & {
  loadingStepOptions: boolean;
  lessonOptions: SelectOption[];
  onRefreshLessons?: () => void;
}) => {
  const dropdownOptions = lessonOptions.map((lesson) => ({
    ...lesson,
    searchText: lesson.label,
    label: (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <span>{lesson.label}</span>
        <a
          href={`/panel/chat-lessons/create/${lesson.value}`}
          target="_blank"
          rel="noopener noreferrer"
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
        >
          Open <ExportOutlined />
        </a>
      </div>
    ),
  }));

  return (
    <Form.Item label="Chat lesson" required>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <Form.Item
          {...restField}
          name={[name, "lessonId"]}
          rules={[{ required: true, message: "Required" }]}
          style={{ flex: 1, marginBottom: 0 }}
        >
          <Select
            showSearch
            placeholder="Select lesson"
            options={dropdownOptions}
            optionFilterProp="searchText"
            loading={loadingStepOptions}
            disabled={loadingStepOptions}
            notFoundContent="No lessons for selected language tab"
            dropdownRender={(menu) => (
              <>
                <div style={{ padding: "8px 12px" }}>
                  <a
                    href="/panel/chat-lessons/create"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <PlusOutlined /> Create new chat lesson
                  </a>
                </div>
                <div style={{ borderTop: "1px solid #f0f0f0" }}>{menu}</div>
              </>
            )}
          />
        </Form.Item>
        <Tooltip title="Refresh chat lessons">
          <Button
            type="text"
            icon={<ReloadOutlined />}
            aria-label="Refresh chat lessons"
            loading={loadingStepOptions}
            onClick={onRefreshLessons}
          />
        </Tooltip>
      </div>
    </Form.Item>
  );
};

const WordCardStepField = ({
  name,
  restField,
  loadingStepOptions,
  wordCardOptions,
  onRefreshLessons,
}: StepListFieldProps & {
  loadingStepOptions: boolean;
  wordCardOptions: SelectOption[];
  onRefreshLessons?: () => void;
}) => {
  const dropdownOptions = wordCardOptions.map((card) => ({
    ...card,
    searchText: card.label,
    label: (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <span>{card.label}</span>
        <a
          href={`/panel/word-cards/create/${card.value}`}
          target="_blank"
          rel="noopener noreferrer"
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
        >
          Open <ExportOutlined />
        </a>
      </div>
    ),
  }));

  return (
    <Form.Item label="Word card" required>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <Form.Item
          {...restField}
          name={[name, "wordCardId"]}
          rules={[{ required: true, message: "Required" }]}
          style={{ flex: 1, marginBottom: 0 }}
        >
          <Select
            showSearch
            placeholder="Select word card"
            options={dropdownOptions}
            optionFilterProp="searchText"
            loading={loadingStepOptions}
            disabled={loadingStepOptions}
            notFoundContent="No word cards"
            dropdownRender={(menu) => (
              <>
                <div style={{ padding: "8px 12px" }}>
                  <a
                    href="/panel/word-cards/create"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <PlusOutlined /> Create new word card
                  </a>
                </div>
                <div style={{ borderTop: "1px solid #f0f0f0" }}>{menu}</div>
              </>
            )}
          />
        </Form.Item>
        <Tooltip title="Refresh word cards">
          <Button
            type="text"
            icon={<ReloadOutlined />}
            aria-label="Refresh word cards"
            loading={loadingStepOptions}
            onClick={onRefreshLessons}
          />
        </Tooltip>
      </div>
    </Form.Item>
  );
};

const StepTypeFields = ({
  name,
  restField,
  type,
  form,
  stepPathPrefix,
  loadingStepOptions,
  lessonOptions,
  onRefreshLessons,
  wordCardOptions,
  characterOptions,
  onOpenStepMediaFileManager,
}: StepListFieldProps & {
  type?: JourneyStepType;
  form: FormInstance;
  stepPathPrefix: (string | number)[];
  loadingStepOptions: boolean;
  lessonOptions: SelectOption[];
  onRefreshLessons?: () => void;
  wordCardOptions: SelectOption[];
  characterOptions: SelectOption[];
  onOpenStepMediaFileManager?: (path: (string | number)[]) => void;
}) => {
  switch (type) {
    case JourneyStepType.ChatLesson:
      return (
        <ChatLessonStepField
          name={name}
          restField={restField}
          loadingStepOptions={loadingStepOptions}
          lessonOptions={lessonOptions}
          onRefreshLessons={onRefreshLessons}
        />
      );
    case JourneyStepType.WordLearning:
      return (
        <WordCardStepField
          name={name}
          restField={restField}
          loadingStepOptions={loadingStepOptions}
          wordCardOptions={wordCardOptions}
          onRefreshLessons={onRefreshLessons}
        />
      );
    case JourneyStepType.CharacterTalk:
      return (
        <>
          <Form.Item
            {...restField}
            name={[name, "characterId"]}
            label="Character"
            rules={[{ required: true, message: "Required" }]}
          >
            <Select
              showSearch
              placeholder="Select character"
              options={characterOptions}
              optionFilterProp="label"
              loading={loadingStepOptions}
              disabled={loadingStepOptions}
              notFoundContent="No characters for selected language tab"
            />
          </Form.Item>
          <Form.Item
            {...restField}
            name={[name, "minDurationMinutes"]}
            label="Min duration (min)"
            rules={[{ required: true, message: "Required" }]}
          >
            <InputNumber min={1} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            {...restField}
            name={[name, "stepCustomInstruction"]}
            label="Step custom instruction"
            tooltip="Step-only rules for what the character must do in this scenario. Character identity, language, and tone come from the selected character."
          >
            <Input.TextArea
              rows={10}
              placeholder={`e.g.\n- Guide ordering at the counter\n- Ask about size and milk options\n- Keep focus on completing the order`}
            />
          </Form.Item>
          <Form.Item
            {...restField}
            name={[name, "whenBeDone"]}
            label="When be done"
            tooltip="Step-only criteria for when this conversation should end — not character or language setup"
          >
            <Input.TextArea
              rows={6}
              placeholder={`e.g.\n- Learner orders a drink\n- Learner chooses a size\n- Learner confirms payment`}
            />
          </Form.Item>
        </>
      );
    case JourneyStepType.MediaTalk:
      return (
        <MediaTalkStepFields
          name={name}
          restField={restField}
          form={form}
          stepPathPrefix={stepPathPrefix}
          characterOptions={characterOptions}
          loadingStepOptions={loadingStepOptions}
          onOpenFileManager={() => onOpenStepMediaFileManager?.(stepPathPrefix)}
        />
      );
    default:
      return null;
  }
};

interface StepMetadataTabsProps extends StepListFieldProps {
  form: FormInstance;
  stepType?: JourneyStepType;
  stepPathPrefix: (string | number)[];
  activeLanguageTab: string;
  onLanguageTabChange: (langKey: string) => void;
  translating: boolean;
  onTranslate: () => void;
  isActive: boolean;
}

const StepMetadataTabs = ({
  name,
  restField,
  form,
  stepType,
  stepPathPrefix,
  activeLanguageTab,
  onLanguageTabChange,
  translating,
  onTranslate,
  isActive,
}: StepMetadataTabsProps) => (
  <div style={METADATA_SECTION_STYLE}>
    <div style={METADATA_SECTION_LABEL_STYLE}>
      Step metadata (shown to learners per language)
    </div>
    <Tabs
      activeKey={activeLanguageTab}
      onChange={onLanguageTabChange}
      tabBarExtraContent={renderMetadataTranslateButton({
        loading: translating,
        onClick: onTranslate,
        tooltip:
          stepType === JourneyStepType.MediaTalk ||
          stepType === JourneyStepType.CharacterTalk
            ? "Translate English step title, description, and starter text to es, fr, de, it, tr, ja, ko, zh"
            : "Translate English step title and description to es, fr, de, it, tr, ja, ko, zh",
      })}
      items={JOURNEY_LANGUAGES.map((lang) => ({
        key: lang.key,
        label: lang.label,
        forceRender: isActive,
        children: (
          <>
            <Form.Item
              {...restField}
              name={[name, "metadata", lang.key, "title"]}
              label="Title"
              rules={[
                {
                  required: lang.key === "en",
                  message: "Required",
                },
              ]}
            >
              <Input placeholder={`Step title in ${lang.label}`} />
            </Form.Item>
            <Form.Item
              {...restField}
              name={[name, "metadata", lang.key, "description"]}
              label="Description"
            >
              <Input.TextArea
                rows={2}
                placeholder={`Optional description in ${lang.label}`}
              />
            </Form.Item>
            {(stepType === JourneyStepType.MediaTalk ||
              stepType === JourneyStepType.CharacterTalk) && (
              <Form.Item
                {...restField}
                name={[name, "metadata", lang.key, "starterText"]}
                label="Starter text"
                tooltip="If set, the app sends this to the AI when the user starts this step"
              >
                <Input.TextArea
                  rows={2}
                  placeholder={`Optional opening line in ${lang.label}`}
                />
              </Form.Item>
            )}
          </>
        ),
      }))}
    />
  </div>
);

interface StepEditorProps extends StepListFieldProps {
  form: FormInstance;
  step: IJourneyStep | undefined;
  watchPath: (string | number)[];
  resourceLanguage?: string;
  onResourceLanguageChange?: (voiceCode: string) => void;
  loadingStepOptions: boolean;
  lessonOptions: SelectOption[];
  onRefreshLessons?: () => void;
  wordCardOptions: SelectOption[];
  characterOptions: SelectOption[];
  requestMetadataTranslations: (payload: {
    title: string;
    description?: string;
    starterText?: string;
  }) => Promise<MetadataTranslation | undefined>;
  onRemove: () => void;
  onOpenStepMediaFileManager?: (path: (string | number)[]) => void;
  showSubSteps?: boolean;
  nestedTitle?: string;
  allowOptional?: boolean;
  isActive: boolean;
  journeyId?: string;
}

const StepEditor = ({
  name,
  restField,
  form,
  step,
  watchPath,
  resourceLanguage,
  onResourceLanguageChange,
  loadingStepOptions,
  lessonOptions,
  onRefreshLessons,
  wordCardOptions,
  characterOptions,
  requestMetadataTranslations,
  onRemove,
  onOpenStepMediaFileManager,
  showSubSteps,
  nestedTitle,
  allowOptional = true,
  isActive,
  journeyId,
}: StepEditorProps) => {
  const type = step?.type ?? JourneyStepType.ChatLesson;
  const meta = stepTypeMeta[type];
  const [activeLanguageTab, setActiveLanguageTab] = useState("en");
  const [translating, setTranslating] = useState(false);
  const stepPathPrefix = [...watchPath, name];

  useEffect(() => {
    if (!allowOptional) {
      form.setFieldValue([...watchPath, name, "isOptional"], false);
    }
  }, [allowOptional, form, watchPath, name, isActive]);

  const handleLanguageTabChange = (langKey: string) => {
    setActiveLanguageTab(langKey);
    onResourceLanguageChange?.(voiceCodeForLanguageKey(langKey));
  };

  const handleTranslate = async () => {
    const stepPath = [...watchPath, name];
    const currentStep = form.getFieldValue(stepPath) as
      | IJourneyStep
      | undefined;
    const stepMetadata = (currentStep?.metadata ?? {}) as IJourneyStepMetadata;
    const enMeta = stepMetadata.en ?? {};
    const enTitle = String(enMeta.title ?? "").trim();
    const enDescription = String(enMeta.description ?? "").trim();
    const enStarterText = String(enMeta.starterText ?? "").trim();

    if (!enTitle) {
      message.error("Fill in the English step title first");
      return;
    }

    setTranslating(true);
    try {
      const translations = await requestMetadataTranslations({
        title: enTitle,
        ...(enDescription ? { description: enDescription } : {}),
        ...((type === JourneyStepType.MediaTalk ||
          type === JourneyStepType.CharacterTalk) &&
        enStarterText
          ? { starterText: enStarterText }
          : {}),
      });
      if (!translations) {
        message.error("AI returned no translations");
        return;
      }
      form.setFieldValue(
        [...stepPath, "metadata"],
        mergeMetadataTranslations(stepMetadata, translations),
      );
      message.success("Translated step metadata from English");
    } catch {
      message.error("Failed to translate step metadata");
    } finally {
      setTranslating(false);
    }
  };

  return (
    <div
      style={{
        background: "#fff",
        border: `1px solid ${meta.color}33`,
        borderTop: `3px solid ${meta.color}`,
        borderRadius: 8,
        padding: 20,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
          gap: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ color: meta.color, fontSize: 18 }}>{meta.icon}</span>
          <strong>
            Step #{name + 1} — {meta.short}
          </strong>
        </div>
        <Button danger icon={<DeleteOutlined />} onClick={onRemove}>
          Remove step
        </Button>
      </div>

      <Form.Item {...restField} name={[name, "stepId"]} hidden>
        <Input />
      </Form.Item>
      <Form.Item {...restField} name={[name, "configs"]} hidden>
        <StepConfigsField />
      </Form.Item>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: allowOptional ? "1fr 1fr 1fr" : "1fr 1fr",
          gap: 16,
          marginBottom: 8,
        }}
      >
        <Form.Item label="Type" required style={{ marginBottom: 0 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <Form.Item
              {...restField}
              name={[name, "type"]}
              rules={[{ required: true, message: "Required" }]}
              style={{ flex: 1, marginBottom: 0 }}
            >
              <Select options={JOURNEY_STEP_TYPE_OPTIONS} />
            </Form.Item>
          </div>
        </Form.Item>
        <Form.Item {...restField} name={[name, "xpReward"]} label="XP reward">
          <InputNumber min={0} style={{ width: "100%" }} />
        </Form.Item>
        {allowOptional ? (
          <Form.Item
            {...restField}
            name={[name, "isOptional"]}
            valuePropName="checked"
            label=" "
          >
            <Checkbox>Optional (skippable)</Checkbox>
          </Form.Item>
        ) : (
          <Form.Item {...restField} name={[name, "isOptional"]} hidden>
            <Input type="hidden" />
          </Form.Item>
        )}
      </div>

      <StepMetadataTabs
        name={name}
        restField={restField}
        form={form}
        stepType={type}
        stepPathPrefix={stepPathPrefix}
        isActive={isActive}
        activeLanguageTab={activeLanguageTab}
        onLanguageTabChange={handleLanguageTabChange}
        translating={translating}
        onTranslate={handleTranslate}
      />

      <StepTypeFields
        name={name}
        restField={restField}
        type={type}
        form={form}
        stepPathPrefix={stepPathPrefix}
        loadingStepOptions={loadingStepOptions}
        lessonOptions={lessonOptions}
        onRefreshLessons={onRefreshLessons}
        wordCardOptions={wordCardOptions}
        characterOptions={characterOptions}
        onOpenStepMediaFileManager={onOpenStepMediaFileManager}
      />

      {showSubSteps && (
        <div
          style={{
            marginTop: 24,
            paddingTop: 20,
            borderTop: "1px dashed #d9d9d9",
          }}
        >
          <Form.List name={[name, "subSteps"]}>
            {(subFields, { add: addSub, remove: removeSub, move: moveSub }) => (
              <JourneyStepsFlowPanel
                form={form}
                fields={subFields}
                move={moveSub}
                remove={removeSub}
                onAdd={() => addSub(defaultJourneyStep(subFields.length))}
                watchPath={[...watchPath, name, "subSteps"]}
                title={nestedTitle ?? "Sub-steps"}
                subtitle={
                  <span style={{ color: "#888", fontSize: 12, marginLeft: 8 }}>
                    Helper activities during this step
                  </span>
                }
                addButtonLabel="Add sub-step"
                emptyHint="No sub-steps yet."
                resourceLanguage={resourceLanguage}
                onResourceLanguageChange={onResourceLanguageChange}
                loadingStepOptions={loadingStepOptions}
                lessonOptions={lessonOptions}
                onRefreshLessons={onRefreshLessons}
                wordCardOptions={wordCardOptions}
                characterOptions={characterOptions}
                requestMetadataTranslations={requestMetadataTranslations}
                onOpenStepMediaFileManager={onOpenStepMediaFileManager}
                compact
                allowOptional={false}
                journeyId={journeyId}
              />
            )}
          </Form.List>
        </div>
      )}
    </div>
  );
};

interface DraggableStepTabProps {
  index: number;
  step: IJourneyStep | undefined;
  isActive: boolean;
  isDragging: boolean;
  isDropTarget: boolean;
  onSelect: () => void;
  onDragStart: (e: DragEvent<HTMLButtonElement>, index: number) => void;
  onDragEnd: () => void;
  onDragOver: (e: DragEvent<HTMLButtonElement>, index: number) => void;
  onDrop: (e: DragEvent<HTMLButtonElement>, index: number) => void;
}

const DraggableStepTab = ({
  index,
  step,
  isActive,
  isDragging,
  isDropTarget,
  onSelect,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
}: DraggableStepTabProps) => {
  const type = step?.type ?? JourneyStepType.ChatLesson;
  const meta = stepTypeMeta[type];
  const title = getStepDisplayTitle(step ?? {});
  const shortTitle =
    title.length > 22 ? `${title.slice(0, 22).trim()}…` : title;

  return (
    <button
      type="button"
      draggable
      onClick={onSelect}
      onDragStart={(e) => onDragStart(e, index)}
      onDragEnd={onDragEnd}
      onDragOver={(e) => onDragOver(e, index)}
      onDrop={(e) => onDrop(e, index)}
      style={{
        flex: "0 0 auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 4,
        minWidth: 120,
        maxWidth: 160,
        padding: "10px 12px",
        borderRadius: 8,
        border: isDropTarget
          ? `2px dashed ${meta.color}`
          : isActive
            ? `2px solid ${meta.color}`
            : `1px solid ${meta.color}44`,
        background: isActive ? `${meta.color}10` : "#fff",
        cursor: isDragging ? "grabbing" : "pointer",
        opacity: isDragging ? 0.5 : 1,
        textAlign: "left",
        transition: "border 0.15s, background 0.15s, opacity 0.15s",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          width: "100%",
        }}
      >
        <span style={{ color: meta.color, fontSize: 14 }}>{meta.icon}</span>
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: "#888",
          }}
        >
          #{index + 1}
        </span>
        <DragOutlined
          style={{ marginLeft: "auto", color: "#bbb", fontSize: 11 }}
        />
      </div>
      <span
        style={{
          fontSize: 12,
          fontWeight: isActive ? 600 : 500,
          color: "#333",
          lineHeight: 1.3,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          width: "100%",
        }}
        title={title}
      >
        {shortTitle || meta.short}
      </span>
    </button>
  );
};

interface JourneyStepsFlowPanelProps {
  form: FormInstance;
  fields: FormListFieldData[];
  move: (from: number, to: number) => void;
  remove: (name: number) => void;
  onAdd: () => void;
  watchPath: (string | number)[];
  title: string;
  subtitle?: ReactNode;
  addButtonLabel?: string;
  emptyHint?: string;
  resourceLanguage?: string;
  onResourceLanguageChange?: (voiceCode: string) => void;
  loadingStepOptions: boolean;
  lessonOptions: SelectOption[];
  onRefreshLessons?: () => void;
  wordCardOptions: SelectOption[];
  characterOptions: SelectOption[];
  requestMetadataTranslations: (payload: {
    title: string;
    description?: string;
    starterText?: string;
  }) => Promise<MetadataTranslation | undefined>;
  onOpenStepMediaFileManager?: (path: (string | number)[]) => void;
  showSubSteps?: boolean;
  nestedTitle?: string;
  compact?: boolean;
  allowOptional?: boolean;
  journeyId?: string;
}

export const JourneyStepsFlowPanel = ({
  form,
  fields,
  move,
  remove,
  onAdd,
  watchPath,
  title,
  subtitle,
  addButtonLabel = "Add step",
  emptyHint,
  resourceLanguage,
  onResourceLanguageChange,
  loadingStepOptions,
  lessonOptions,
  onRefreshLessons,
  wordCardOptions,
  characterOptions,
  requestMetadataTranslations,
  onOpenStepMediaFileManager,
  showSubSteps = false,
  nestedTitle,
  compact = false,
  allowOptional = true,
  journeyId,
}: JourneyStepsFlowPanelProps) => {
  const steps = (Form.useWatch(watchPath, form) ?? []) as IJourneyStep[];
  const [activeIndex, setActiveIndex] = useState(0);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);

  useEffect(() => {
    if (fields.length === 0) {
      setActiveIndex(0);
      return;
    }
    if (activeIndex >= fields.length) {
      setActiveIndex(Math.max(0, fields.length - 1));
    }
  }, [fields.length, activeIndex]);

  const handleMove = (from: number, to: number) => {
    move(from, to);
    setActiveIndex((prev) => adjustIndexAfterMove(prev, from, to));
  };

  const handleRemove = (index: number) => {
    remove(index);
    setActiveIndex((prev) => {
      if (fields.length <= 1) return 0;
      if (prev > index) return prev - 1;
      if (prev === index) return Math.max(0, index - 1);
      return prev;
    });
  };

  const handleAdd = () => {
    onAdd();
    setActiveIndex(fields.length);
  };

  const handleDragStart = (e: DragEvent<HTMLButtonElement>, index: number) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(index));
    setDragIndex(index);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setDropIndex(null);
  };

  const handleDragOver = (e: DragEvent<HTMLButtonElement>, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dropIndex !== index) setDropIndex(index);
  };

  const handleDrop = (e: DragEvent<HTMLButtonElement>, toIndex: number) => {
    e.preventDefault();
    e.stopPropagation();
    const fromIndex = Number(e.dataTransfer.getData("text/plain"));
    if (!Number.isNaN(fromIndex) && fromIndex !== toIndex) {
      handleMove(fromIndex, toIndex);
    }
    setDragIndex(null);
    setDropIndex(null);
  };

  return (
    <div
      style={{
        marginBottom: compact ? 0 : 24,
        padding: compact ? 0 : 16,
        background: compact ? "transparent" : "#fafafa",
        border: compact ? "none" : "1px solid #f0f0f0",
        borderRadius: 8,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <div>
          <strong>{title}</strong>
          {subtitle}
        </div>
        <Button
          type="dashed"
          size="small"
          icon={<PlusOutlined />}
          onClick={handleAdd}
        >
          {addButtonLabel}
        </Button>
      </div>

      {fields.length === 0 ? (
        <div style={{ color: "#888", fontSize: 13, padding: "8px 0" }}>
          {emptyHint ??
            "No steps yet. Add your first step to build the journey path."}
        </div>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              gap: 8,
              overflowX: "auto",
              paddingBottom: 8,
              marginBottom: 16,
            }}
          >
            {fields.map(({ key, name }) => (
              <DraggableStepTab
                key={key}
                index={name}
                step={steps[name]}
                isActive={activeIndex === name}
                isDragging={dragIndex === name}
                isDropTarget={dropIndex === name && dragIndex !== name}
                onSelect={() => setActiveIndex(name)}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              />
            ))}
          </div>

          {fields.map(({ key, name, ...restField }) => (
            <div
              key={key}
              style={{ display: activeIndex === name ? "block" : "none" }}
            >
              <StepEditor
                name={name}
                restField={restField}
                form={form}
                step={steps[name]}
                watchPath={watchPath}
                resourceLanguage={resourceLanguage}
                onResourceLanguageChange={onResourceLanguageChange}
                loadingStepOptions={loadingStepOptions}
                lessonOptions={lessonOptions}
                onRefreshLessons={onRefreshLessons}
                wordCardOptions={wordCardOptions}
                characterOptions={characterOptions}
                requestMetadataTranslations={requestMetadataTranslations}
                onOpenStepMediaFileManager={onOpenStepMediaFileManager}
                onRemove={() => handleRemove(name)}
                showSubSteps={showSubSteps}
                nestedTitle={nestedTitle}
                allowOptional={allowOptional}
                isActive={activeIndex === name}
                journeyId={journeyId}
              />
            </div>
          ))}
        </>
      )}
    </div>
  );
};

interface JourneyStepsFlowProps {
  form: FormInstance;
  fields: FormListFieldData[];
  move: (from: number, to: number) => void;
  remove: (name: number) => void;
  onAdd: () => void;
  resourceLanguage?: string;
  resourceLanguageLabel?: string;
  onResourceLanguageChange?: (voiceCode: string) => void;
  loadingStepOptions: boolean;
  lessonOptions: SelectOption[];
  onRefreshLessons?: () => void;
  wordCardOptions: SelectOption[];
  characterOptions: SelectOption[];
  requestMetadataTranslations: (payload: {
    title: string;
    description?: string;
    starterText?: string;
  }) => Promise<MetadataTranslation | undefined>;
  onOpenStepMediaFileManager?: (path: (string | number)[]) => void;
  journeyId?: string;
}

const JourneyStepsFlow = ({
  form,
  fields,
  move,
  remove,
  onAdd,
  resourceLanguage,
  resourceLanguageLabel,
  onResourceLanguageChange,
  loadingStepOptions,
  lessonOptions,
  onRefreshLessons,
  wordCardOptions,
  characterOptions,
  requestMetadataTranslations,
  onOpenStepMediaFileManager,
  journeyId,
}: JourneyStepsFlowProps) => {
  const resourceSubtitle = (
    <span style={{ color: "#888", fontSize: 12, marginLeft: 8 }}>
      Step resources use {resourceLanguageLabel ?? "English"} tab
      {loadingStepOptions ? " (loading…)" : ""}
    </span>
  );

  return (
    <JourneyStepsFlowPanel
      form={form}
      fields={fields}
      move={move}
      remove={remove}
      onAdd={onAdd}
      watchPath={["steps"]}
      title="Journey flow"
      subtitle={resourceSubtitle}
      resourceLanguage={resourceLanguage}
      onResourceLanguageChange={onResourceLanguageChange}
      loadingStepOptions={loadingStepOptions}
      lessonOptions={lessonOptions}
      onRefreshLessons={onRefreshLessons}
      wordCardOptions={wordCardOptions}
      characterOptions={characterOptions}
      requestMetadataTranslations={requestMetadataTranslations}
      onOpenStepMediaFileManager={onOpenStepMediaFileManager}
      showSubSteps
      nestedTitle="Sub-steps"
      journeyId={journeyId}
    />
  );
};

export default JourneyStepsFlow;
